import { get, pick } from "lodash"
import { testSaga } from "redux-saga-test-plan"
import { LOADING, OK } from "helpers/data-statuses"
import { Creators as DataCreators } from "../data"
import { Creators as GraphqlCreators } from "../graphql"
import { INITIAL_STATE, CANCELLED, handleCleanupData, handleQueryExecuted, handleRegisterQuery, handleSetRefreshStatus, REFRESHING, handleUnregisterQuery, handleRefreshQuery, handleCancelQuery, handleRegisterQuerySaga, Creators, sagaSelectors, closeAllSockets, openSocket, handleUnregisterQuerySaga, handleUnregisterModuleQueries, handleRefreshData, handleRefreshQuerySaga, handleAllQueriesExecuted } from "../intraday"
import { select } from "redux-saga/effects"
import { sendQuery } from "api/graphql"
import { get as apiGet, post as apiPost } from "api"

const defaultVars = {
  channel: "account_resource",
  resource: "resource",
  module: "module",
  currentDataIdx: 0,
  refreshIdx: 0,
  selectors: [
    select(sagaSelectors.getRefresh),
    select(sagaSelectors.getQueries),
  ],
}

const graphQlAction = {
  channel: defaultVars.channel,
  resource: defaultVars.resource,
  queryInfo: {
    id: "graphqlQuery",
    query: "query { }",
    variables: {},
    account: "account",
    options: null,
    beta: false,
    isGraphQl: true,
  },
}

const apiAction = {
  channel: defaultVars.channel,
  resource: defaultVars.resource,
  queryInfo: {
    id: "apiQuery",
    url: "http://localhost:3000",
    payload: { param: "value" },
    noToken: false,
    isGraphQl: false,
  },
}

describe("Intraday Model Tests", () => {
  describe("handleCleanupData", () => {
    const initialState = {
      data: {
        [defaultVars.channel]: {
          0: {
            resource1: {},
          },
          1: {
            resource1: {},
            resource2: {},
          },
        },
      },
    }

    it("Should remove the resource to cleanup but keep the index when other resource exists", () => {
      const updatedState = handleCleanupData(initialState, { channel: defaultVars.channel, idx: 1, resource: "resource2" })
      expect(updatedState).toEqual({
        data: {
          [defaultVars.channel]: {
            0: {
              resource1: {},
            },
            1: {
              resource1: {},
            },
          },
        },
      })
    })

    it("Should remove the resource to cleanup and remove the index when no resources are left", () => {
      const updatedState = handleCleanupData(initialState, { channel: defaultVars.channel, idx: 0, resource: "resource1" })
      expect(updatedState).toEqual({
        data: {
          [defaultVars.channel]: {
            1: {
              resource1: {},
              resource2: {},
            },
          },
        },
      })
    })

    it("Should remove the index when no resource is specified", () => {
      const updatedState = handleCleanupData(initialState, { channel: defaultVars.channel, idx: 1 })
      expect(updatedState).toEqual({
        data: {
          [defaultVars.channel]: {
            0: {
              resource1: {},
            },
          },
        },
      })
    })

    it("Should not change the state when index does not exist", () => {
      const updatedState = handleCleanupData(initialState, { channel: defaultVars.channel, idx: 2 })
      expect(updatedState).toEqual(initialState)
    })

    it("Should not change the state when no index is passed", () => {
      const updatedState = handleCleanupData(initialState, { channel: defaultVars.channel })
      expect(updatedState).toEqual(initialState)
    })
  })

  describe("handleQueryExecuted", () => {
    const initialState = {
      data: {
        [defaultVars.channel]: {
          0: {
            okResource: {
              some: "data",
              status: OK,
            },
            cancelledResource: {
              some: "data",
              status: CANCELLED,
            },
          },
        },
      },
    }

    const executeReducer = (resource, data) => handleQueryExecuted(initialState, { channel: defaultVars.channel, idx: 0, resource, data })

    it("Should update the data of the executed query resource", () => {
      const resource = "okResource"
      const updatedData = executeReducer(resource, { new: "data", status: OK })
      expect(get(updatedData, ["data", defaultVars.channel, "0", resource])).toEqual({ new: "data", status: OK })
    })

    it("Should not update the data of the executed query resource when it was cancelled", () => {
      const resource = "cancelledResource"
      const updatedData = executeReducer(resource, { new: "data", status: OK })
      expect(get(updatedData, ["data", defaultVars.channel, "0", resource])).toEqual({ some: "data", status: CANCELLED })
    })

    it("Should create the channel, index and resource when it does not already exist", () => {
      const updatedData = handleQueryExecuted(initialState, { channel: "newChannel", idx: 0, resource: "newResource", data: { some: "data", status: OK } })
      expect(updatedData).toEqual({
        data: {
          ...initialState.data,
          newChannel: {
            0: {
              newResource: {
                some: "data",
                status: OK,
              },
            },
          },
        },
      })
    })

    it("Should create the channel, index and resource when it does not already exist even if it was cancelled", () => {
      const updatedData = handleQueryExecuted(initialState, { channel: "newChannel", idx: 0, resource: "newResource", data: { some: "data", status: CANCELLED } })
      expect(updatedData).toEqual({
        data: {
          ...initialState.data,
          newChannel: {
            0: {
              newResource: {
                some: "data",
                status: CANCELLED,
              },
            },
          },
        },
      })
    })
  })

  describe("handleRegisterQuery", () => {
    const initialState = {
      queries: {
        [defaultVars.channel]: {
          [defaultVars.resource]: {
            modules: [defaultVars.module],
            query: "info",
          },
        },
      },
    }

    const executeReducer = (module, channel, resource, queryInfo) => handleRegisterQuery(initialState, { module, channel, resource, queryInfo })

    it("Registers a new query when it does not exist", () => {
      const updatedState = executeReducer(defaultVars.module, defaultVars.channel, "newResource", { query: "info" })
      expect(updatedState).toEqual({
        queries: {
          [defaultVars.channel]: {
            ...initialState.queries[defaultVars.channel],
            newResource: {
              modules: [defaultVars.module],
              query: "info",
            },
          },
        },
      })
    })

    it("Updates a query's information when it exists for a module", () => {
      const updatedState = executeReducer(defaultVars.module, defaultVars.channel, defaultVars.resource, { query: "new-info" })
      expect(updatedState).toEqual({
        queries: {
          [defaultVars.channel]: {
            [defaultVars.resource]: {
              modules: [defaultVars.module],
              query: "new-info",
            },
          },
        },
      })
    })

    it("Registers a query for a new module, and updates it", () => {
      const updatedState = executeReducer("newModule", defaultVars.channel, defaultVars.resource, { query: "new-info" })
      expect(updatedState).toEqual({
        queries: {
          [defaultVars.channel]: {
            [defaultVars.resource]: {
              modules: [defaultVars.module, "newModule"],
              query: "new-info",
            },
          },
        },
      })
    })

    it("Registers a query for a new channel", () => {
      const updatedState = executeReducer(defaultVars.module, "newChannel", defaultVars.resource, { query: "info" })
      expect(updatedState).toEqual({
        queries: {
          ...initialState.queries,
          newChannel: {
            [defaultVars.resource]: {
              modules: [defaultVars.module],
              query: "info",
            },
          },
        },
      })
    })
  })

  describe("handleSetRefreshStatus", () => {
    it("Updates the refresh status correctly", () => {
      const updatedState = handleSetRefreshStatus(INITIAL_STATE, { status: REFRESHING, channel: defaultVars.channel, refreshIdx: defaultVars.refreshIdx, currentDataIdx: null })
      expect(updatedState).toEqual({
        ...INITIAL_STATE,
        refresh: {
          status: REFRESHING,
          channel: defaultVars.channel,
          refreshIdx: defaultVars.refreshIdx,
          currentDataIdx: null,
        },
      })
    })
  })

  describe("handleUnregisterQuery", () => {
    const initialState = {
      ...INITIAL_STATE,
      refresh: {
        status: OK,
        currentDataIdx: defaultVars.currentDataIdx,
      },
      data: {
        channel1: {
          0: {
            resource3: {
              data: {},
            },
          },
        },
      },
      queries: {
        channel1: {
          resource1: {
            modules: ["module1"],
            query: "info",
          },
          resource2: {
            modules: ["module1", "module2"],
            query: "info",
          },
          resource3: {
            modules: ["module1"],
            query: "info",
          },
        },
        channel2: {
          [defaultVars.resource]: {
            modules: [defaultVars.module],
            query: "info",
          },
        },
      },
    }

    const executeReducer = (module, channel, resource) => handleUnregisterQuery(initialState, { module, channel, resource })

    it("Removes the query for a module, but keeps the resource if there is another module registered to it", () => {
      const updatedState = executeReducer("module1", "channel1", "resource2")
      expect(updatedState).toEqual({
        ...initialState,
        queries: {
          ...initialState.queries,
          channel1: {
            ...initialState.queries.channel1,
            resource2: {
              modules: ["module2"],
              query: "info",
            },
          },
        },
      })
    })

    it("Removes the query for a module, and removes the resource if it was the only module registered to it", () => {
      const updatedState = executeReducer("module1", "channel1", "resource1")
      expect(updatedState).toEqual({
        ...initialState,
        queries: {
          ...initialState.queries,
          channel1: {
            resource2: {
              modules: ["module1", "module2"],
              query: "info",
            },
            resource3: {
              modules: ["module1"],
              query: "info",
            },
          },
        },
      })
    })

    it("Removes the query for a module, removes the resource if it was the only module registered to it, and cleans up the data for that resource", () => {
      const updatedState = executeReducer("module1", "channel1", "resource3")
      expect(updatedState).toEqual({
        ...initialState,
        data: {
          channel1: {},
        },
        queries: {
          ...initialState.queries,
          channel1: {
            resource1: {
              modules: ["module1"],
              query: "info",
            },
            resource2: {
              modules: ["module1", "module2"],
              query: "info",
            },
          },
        },
      })
    })

    it("Removes the query and resource, and removes the channel if there is no query left on it", () => {
      const updatedState = executeReducer(defaultVars.module, "channel2", defaultVars.resource)
      expect(updatedState).toEqual({
        ...initialState,
        queries: {
          ...pick(initialState.queries, ["channel1"]),
        },
      })
    })
  })

  describe("handleRefreshQuery", () => {
    it("Sets the status to LOADING for a refreshing query", () => {
      const updatedState = handleRefreshQuery(INITIAL_STATE, { channel: defaultVars.channel, idx: 0, resource: defaultVars.resource })
      expect(updatedState).toEqual({
        ...INITIAL_STATE,
        data: {
          [defaultVars.channel]: {
            0: {
              [defaultVars.resource]: {
                status: LOADING,
              },
            },
          },
        },
      })
    })
  })

  describe("handleCancelQuery", () => {
    it("Sets the status to CANCELLED for a cancelled query", () => {
      const updatedState = handleCancelQuery(INITIAL_STATE, { channel: defaultVars.channel, idx: 0, resource: defaultVars.resource })
      expect(updatedState).toEqual({
        ...INITIAL_STATE,
        data: {
          [defaultVars.channel]: {
            0: {
              [defaultVars.resource]: {
                status: CANCELLED,
              },
            },
          },
        },
      })
    })
  })

  describe("handleRegisterQuerySaga", () => {
    const refreshingSelectorsReturn = [
      {
        status: REFRESHING,
        refreshIdx: defaultVars.refreshIdx,
      },
      {
        [defaultVars.channel]: {
          [defaultVars.resource]: {},
        },
      },
    ]

    beforeEach(() => {
      // Cleanup the sockets to make sure it is empty before each test
      closeAllSockets()
    })

    it("Cancels the query if a refresh is currently running", () => {
      const { channel, resource, queryInfo: { id, query, variables, account, options, beta } } = graphQlAction
      testSaga(handleRegisterQuerySaga, graphQlAction)
        .next()
        .all(defaultVars.selectors)
        .next(refreshingSelectorsReturn)
        .put(Creators.cancelQuery(channel, 0, resource))
        .next()
        .put(GraphqlCreators.load(resource, id, query, variables, account, options, beta))
        .next()
        .put(Creators.initIntradayListener(channel))
        .next()
        .isDone()
    })

    it("Registers the graphQL query and runs it", () => {
      const { channel, resource, queryInfo: { id, query, variables, account, options, beta } } = graphQlAction
      testSaga(handleRegisterQuerySaga, graphQlAction)
        .next()
        .all(defaultVars.selectors)
        .next([{}, null])
        .put(GraphqlCreators.load(resource, id, query, variables, account, options, beta))
        .next()
        .put(Creators.initIntradayListener(channel))
        .next()
        .isDone()
    })

    it("Registers the api query and runs it", () => {
      const { channel, resource, queryInfo: { id, url, payload, noToken } } = apiAction
      testSaga(handleRegisterQuerySaga, apiAction)
        .next()
        .all(defaultVars.selectors)
        .next([{}, null])
        .put(DataCreators.load(resource, id, url, payload, noToken))
        .next()
        .put(Creators.initIntradayListener(channel))
        .next()
        .isDone()
    })

    it("Registers the graphQL query and runs it, but does not initialize the listener if a socket already exists", () => {
      const { channel, resource, queryInfo: { id, query, variables, account, options, beta } } = graphQlAction
      openSocket(channel)
      testSaga(handleRegisterQuerySaga, graphQlAction)
        .next()
        .all(defaultVars.selectors)
        .next([{}, null])
        .put(GraphqlCreators.load(resource, id, query, variables, account, options, beta))
        .next()
        .isDone()
    })
  })

  describe("handleUnregisterQuerySaga", () => {
    it("Cancels the query if a refresh is currently running", () => {
      testSaga(handleUnregisterQuerySaga, { resource: defaultVars.resource })
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ refresh: { status: REFRESHING, channel: defaultVars.channel, refreshIdx: defaultVars.refreshIdx }, queries: { [defaultVars.channel]: { [defaultVars.resource]: {} } } })
        .put(Creators.cancelQuery(defaultVars.channel, defaultVars.refreshIdx, defaultVars.resource))
        .next()
        .isDone()
    })

    it("Sets the resource data to all nulls if it is no longer registered", () => {
      testSaga(handleUnregisterQuerySaga, { resource: defaultVars.resource })
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ refresh: { status: OK }, queries: {} })
        .put(GraphqlCreators.loadSuccess(defaultVars.resource, null, null, null, null))
        .next()
        .isDone()
    })
  })

  describe("handleUnregisterModuleQueries", () => {
    it("Unregisters queries for the selected module, if it is the only module still using it", () => {
      const module = "module1"
      const queries = {
        [defaultVars.channel]: {
          resource1: {
            modules: [module],
          },
          resource2: {
            modules: [module],
          },
          resource3: {
            modules: [module, "module2"],
          },
          // Should keep since module1 is not using it
          resource4: {
            modules: ["module2"],
          },
        },
      }
      testSaga(handleUnregisterModuleQueries, { module })
        .next()
        .select(sagaSelectors.getQueries)
        .next(queries)
        .put(Creators.unregisterQuery(module, defaultVars.channel, "resource1"))
        .next()
        .put(Creators.unregisterQuery(module, defaultVars.channel, "resource2"))
        .next()
        .put(Creators.unregisterQuery(module, defaultVars.channel, "resource3"))
        .next()
        .isDone()
    })
  })

  describe("handleRefreshData", () => {
    const queries = {
      [defaultVars.channel]: {
        resource1: {
          query: "info",
        },
        resource2: {
          query: "info",
        },
      },
    }

    it("Should not do anything if there are no queries to refresh", () => {
      testSaga(handleRefreshData, { channel: defaultVars.channel })
        .next()
        .all(defaultVars.selectors)
        .next([
          { currentDataIdx: 0, status: OK },
          { channel: {} },
        ])
        .isDone()
    })

    it("Should not do anything if a refresh is ongoing", () => {
      testSaga(handleRefreshData, { channel: defaultVars.channel })
        .next()
        .all(defaultVars.selectors)
        .next([
          { currentDataIdx: defaultVars.currentDataIdx, status: REFRESHING },
          queries,
        ])
        .isDone()
    })

    it("Should change the status to refreshing and trigger the refresh for all queries of the channel", () => {
      const refreshIdx = defaultVars.currentDataIdx + 1
      testSaga(handleRefreshData, { channel: defaultVars.channel })
        .next()
        .all(defaultVars.selectors)
        .next([
          { currentDataIdx: defaultVars.currentDataIdx, status: OK },
          queries,
        ])
        .put(Creators.setRefreshStatus(REFRESHING, defaultVars.channel, refreshIdx, defaultVars.currentDataIdx))
        .next()
        .put(Creators.refreshQuery(defaultVars.channel, refreshIdx, "resource1", queries[defaultVars.channel].resource1))
        .next()
        .put(Creators.refreshQuery(defaultVars.channel, refreshIdx, "resource2", queries[defaultVars.channel].resource2))
        .next()
        .isDone()
    })
  })

  describe("handleRefreshQuerySaga", () => {
    const defaultParams = {
      channel: defaultVars.channel,
      resource: defaultVars.resource,
      idx: defaultVars.refreshIdx,
    }

    const defaultIntradaySelectorResult = {
      data: {
        [defaultParams.channel]: {
          [defaultParams.idx]: {
            [defaultParams.resource]: {
              status: OK,
            },
          },
        },
      },
      refresh: {
        status: REFRESHING,
      },
    }

    it("Executes a graphql query, but nothing more if not all queries are completed", () => {
      const { queryInfo } = graphQlAction
      const { id, query, variables, account, beta } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo })
        .next()
        .call(sendQuery, query, variables, account, beta)
        .next({ data: "result" })
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: OK, data: "result" }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next({
          ...defaultIntradaySelectorResult,
          data: {
            [defaultParams.channel]: {
              [defaultParams.idx]: {
                ...get(defaultIntradaySelectorResult, [defaultParams.channel, defaultParams.idx]),
                resource1: {
                  status: LOADING,
                },
              },
            },
          },
        })
        .isDone()
    })

    it("Executes a graphql query, and calls allQueriesExecuted action when all queries are done", () => {
      const { queryInfo } = graphQlAction
      const { id, query, variables, account, beta } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo })
        .next()
        .call(sendQuery, query, variables, account, beta)
        .next({ data: "result" })
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: OK, data: "result" }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next(defaultIntradaySelectorResult)
        .put(Creators.allQueriesExecuted(defaultParams.channel, defaultParams.idx))
        .next()
        .isDone()
    })

    it("Executes a POST API query", () => {
      const { queryInfo } = apiAction
      const { id, url, payload, noToken } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo })
        .next()
        .call(apiPost, url, payload, noToken)
        .next("result")
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: OK, data: "result" }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next(defaultIntradaySelectorResult)
        .put(Creators.allQueriesExecuted(defaultParams.channel, defaultParams.idx))
        .next()
        .isDone()
    })

    it("Executes a GET API query", () => {
      const { queryInfo } = apiAction
      const { id, url, noToken } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo: { ...queryInfo, payload: null } })
        .next()
        .call(apiGet, url, noToken)
        .next("result")
        .put(Creators.queryExecuted(defaultParams.channel, defaultParams.idx, defaultParams.resource, { id, status: OK, data: "result" }))
        .next()
        .select(sagaSelectors.getIntraday)
        .next(defaultIntradaySelectorResult)
        .put(Creators.allQueriesExecuted(defaultParams.channel, defaultParams.idx))
        .next()
        .isDone()
    })

    it("Executes a POST API query and handles an error", () => {
      const { queryInfo } = apiAction
      const { id, url, payload, noToken } = queryInfo
      testSaga(handleRefreshQuerySaga, { ...defaultParams, queryInfo })
        .next()
        .call(apiPost, url, payload, noToken)
        .throw("Error")
        .put(DataCreators.loadError(defaultParams.resource, id, "Error"))
        .next()
        .select(sagaSelectors.getIntraday)
        .next(defaultIntradaySelectorResult)
        .put(Creators.allQueriesExecuted(defaultParams.channel, defaultParams.idx))
        .next()
        .isDone()
    })
  })

  describe("handleAllQueriesExecuted", () => {
    const dataSomeCancelled = {
      [defaultVars.channel]: {
        [defaultVars.refreshIdx]: {
          resource1: {
            data: "data resource 1",
            status: OK,
          },
          resource2: {
            status: CANCELLED,
          },
          resource3: {
            data: "data resource 3",
            status: OK,
          },
        },
      },
    }

    const dataAllCancelled = {
      [defaultVars.channel]: {
        [defaultVars.refreshIdx]: {
          [defaultVars.resource]: {
            data: "data resource",
            status: CANCELLED,
          },
        },
      },
    }

    const queries = {
      [defaultVars.channel]: {
        resource1: {
          keepDataHistory: true,
        },
        resource2: {
          keepDataHistory: false,
        },
        resource3: {
          keepDataHistory: false,
        },
      },
    }

    it("Transfers the data of all successfully executed queries to global.data, with some CANCELLED queries", () => {
      testSaga(handleAllQueriesExecuted, { channel: defaultVars.channel, idx: defaultVars.refreshIdx })
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ data: dataSomeCancelled, queries, refresh: { currentDataIdx: null } })
        .put(DataCreators.intradayLoadSuccess({
          resource1: {
            data: "data resource 1",
            status: OK,
          },
          resource3: {
            data: "data resource 3",
            status: OK,
          },
        }))
        .next()
        .put(Creators.setRefreshStatus(OK, null, null, defaultVars.refreshIdx))
        .next()
    })

    it("Does not transfer the data with all queries cancelled", () => {
      testSaga(handleAllQueriesExecuted, { channel: defaultVars.channel, idx: defaultVars.refreshIdx })
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ data: dataAllCancelled, queries, refresh: { currentDataIdx: null } })
        .put(Creators.setRefreshStatus(OK, null, null, defaultVars.refreshIdx))
        .next()
    })

    it("Transfers the data of all successfully executed queries to global.data and cleans up the data that is not marked keepDataHistory", () => {
      const data = {
        [defaultVars.channel]: {
          [defaultVars.refreshIdx]: {
            ...get(dataSomeCancelled, [defaultVars.channel, defaultVars.refreshIdx]),
          },
          1: {
            ...get(dataSomeCancelled, [defaultVars.channel, defaultVars.refreshIdx]),
          },
        },
      }
      testSaga(handleAllQueriesExecuted, { channel: defaultVars.channel, idx: 1 })
        .next()
        .select(sagaSelectors.getIntraday)
        .next({ data, queries, refresh: { currentDataIdx: defaultVars.currentDataIdx } })
        .put(DataCreators.intradayLoadSuccess({
          resource1: {
            data: "data resource 1",
            status: OK,
          },
          resource3: {
            data: "data resource 3",
            status: OK,
          },
        }))
        .next()
        .put(Creators.cleanupData(defaultVars.channel, defaultVars.currentDataIdx, "resource2"))
        .next()
        .put(Creators.cleanupData(defaultVars.channel, defaultVars.currentDataIdx, "resource3"))
        .next()
        .put(Creators.setRefreshStatus(OK, null, null, 1))
        .next()
    })
  })
})
